import React, { useEffect, useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import Navbar from '../components/layout/Navbar3';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AmbientParticles from "../components/ui/AmbientParticle";
gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    quote: "The AI-driven insights have completely transformed our workflow. We've cut down our project analysis time by 60% and can now focus on strategic growth rather than getting bogged down in data. It's an essential tool for any forward-thinking business.",
    name: 'Sarah Johnson',
    avatarUrl: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1',
    rating: 5,
  },
  {
    quote: "Initially, we were skeptical, but the results speak for themselves. Our team's productivity has skyrocketed, and the AI's predictive analytics have given us a significant competitive edge. It's not just a product; it's a partnership in our success.",
    name: 'Michael Chen',
    avatarUrl: 'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1',
    rating: 5,
  },
  {
    quote: "Implementing this AI solution was the single best decision we made this year. It seamlessly integrated with our existing tools and provided actionable intelligence from day one. Our revenue is up 20% quarter-over-quarter, directly attributable to the efficiencies gained.",
    name: 'Emily Rodriguez',
    avatarUrl: 'https://images.pexels.com/photos/1065084/pexels-photo-1065084.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1',
    rating: 5,
  },
];

const TestimonialCard = ({ quote, name, avatarUrl, rating }) => (
  <div className="gsap-testimonial group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-8 flex flex-col h-full hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
    {/* Hover glow aura */}
    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
    
    {/* Quote icon */}
    <div className="mb-6">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-blue-500/40">
        <path d="M10 11H6C6 8.23858 8.23858 6 11 6V4C7.13401 4 4 7.13401 4 11V18H10V11ZM20 11H16C16 8.23858 18.2386 6 21 6V4C17.134 4 14 7.13401 14 11V18H20V11Z" fill="currentColor"/>
      </svg>
    </div>

    <div className="flex-grow mb-6">
      <p className="text-gray-300 text-lg leading-relaxed font-light">"{quote}"</p>
    </div>

    {/* Star rating */}
    <div className="flex gap-1 mb-4">
      {Array.from({ length: rating }).map((_, i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-amber-400">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
    </div>

    <div className="flex items-center mt-auto pt-4 border-t border-white/5">
      <img 
        src={avatarUrl} 
        alt={`Avatar of ${name}`} 
        width="56"
        height="56"
        loading="lazy"
        decoding="async"
        className="w-14 h-14 rounded-full border-2 border-white/10 object-cover group-hover:border-blue-500/30 transition-colors duration-300"
        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100/E2E8F0/4A5568?text=User'; }}
      />
      <div className="ml-4">
        <p className="font-semibold text-white text-lg">{name}</p>
        <p className="text-blue-400/60 text-sm font-medium">Verified Customer</p>
      </div>
    </div>
  </div>
);

const TestimonialsPage = () => {
  const headerRef = useRef(null);
  const subRef = useRef(null);
  const gridRef = useRef(null);
  const featuresRef = useRef(null);
  const featuresTitleRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header entrance
      gsap.fromTo(
        headerRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );
      gsap.fromTo(
        subRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", delay: 0.4 }
      );

      // Testimonial cards staggered 3D reveal
      gsap.fromTo(
        ".gsap-testimonial",
        { y: 80, opacity: 0, rotateX: 10, transformPerspective: 1200 },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          duration: 0.9,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 85%",
          },
        }
      );

      // Features title reveal
      gsap.fromTo(
        featuresTitleRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: featuresTitleRef.current,
            start: "top 85%",
          },
        }
      );

      // Feature cards staggered reveal
      gsap.fromTo(
        ".gsap-feature",
        { y: 50, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.7,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 85%",
          },
        }
      );
    });

    // Mouse parallax for background blobs
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
   <div className="relative min-h-screen bg-slate-950">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />
      <AmbientParticles />
      {/* Background Glow Blobs with mouse parallax */}
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none z-0" />
      <Navbar />
      <div className=" min-h-screen font-sans antialiased relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">

          {/* --- Header Section --- */}
          <header className="text-center mb-16 sm:mb-20 pt-8">
          
            <h1 
              ref={headerRef}
              className="font-inter text-4xl sm:text-5xl lg:text-7xl font-black text-gradient-mask tracking-tight opacity-0"
            >
              Don't Just Take Our Word For It.
            </h1>
            <p 
              ref={subRef}
              className="mt-6 max-w-3xl mx-auto text-lg sm:text-xl text-gray-400 font-light leading-relaxed opacity-0"
            >
              See how leading businesses are leveraging our <span className="font-playfair italic text-white/70">AI-powered solutions</span> to drive growth, enhance productivity, and redefine their industries.
            </p>
          </header>

          {/* Section Divider */}
          <div className="relative py-8 flex items-center justify-center overflow-hidden mb-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
            </div>
            <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse" />
          </div>

          {/* --- Testimonials Grid --- */}
          <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} {...testimonial} />
            ))}
          </div>

          {/* Section Divider */}
          <div className="relative py-12 flex items-center justify-center overflow-hidden mt-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
            </div>
            <div className="relative z-10 w-2 h-2 rounded-full bg-purple-500/50 shadow-[0_0_12px_rgba(168,85,247,0.5)] animate-pulse" />
          </div>

          {/* --- Features/Benefits Section --- */}
          <section ref={featuresRef} className="mt-16 sm:mt-20 text-center">
            <div ref={featuresTitleRef} className="opacity-0">
              <h2 className="font-inter text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-mask tracking-tight">
                Unlock Your Business's Full Potential
              </h2>
              <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-400 font-light">
                Our AI solution is designed to integrate seamlessly and deliver immediate value.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              <div className="gsap-feature group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 p-8 rounded-2xl hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white text-xl mb-2">Automated Insights</h3>
                <p className="text-gray-400 leading-relaxed">Turn complex data into clear, actionable steps for your team.</p>
              </div>
              <div className="gsap-feature group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 p-8 rounded-2xl hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                <div className="w-12 h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="font-semibold text-white text-xl mb-2">Enhanced Productivity</h3>
                <p className="text-gray-400 leading-relaxed">Eliminate manual tasks and free up your team for high-impact work.</p>
              </div>
              <div className="gsap-feature group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 p-8 rounded-2xl hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                <div className="w-12 h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-semibold text-white text-xl mb-2">Predictive Forecasting</h3>
                <p className="text-gray-400 leading-relaxed">Make smarter, data-driven decisions with accurate future-state analysis.</p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TestimonialsPage;